import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Settings2, 
  Plus,
  Edit2,
  Trash2,
  CreditCard,
  User,
  Power,
  PowerOff
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const TellerConfigPage = ({ user, onLogout }) => {
  const [counsellors, setCounsellors] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [modalType, setModalType] = useState(null); // 'counsellor' | 'payment-method'
  const [editingItem, setEditingItem] = useState(null);
  const [itemName, setItemName] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [counsellorsRes, methodsRes] = await Promise.all([
        api.get('/counsellors'),
        api.get('/payment-methods')
      ]);
      setCounsellors(counsellorsRes.data);
      setPaymentMethods(methodsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setItemName(item?.name || '');
  };

  const closeModal = () => {
    setModalType(null);
    setEditingItem(null);
    setItemName('');
  };

  const handleSave = async () => {
    if (!itemName.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      const endpoint = modalType === 'counsellor' ? '/counsellors' : '/payment-methods';
      
      if (editingItem) {
        await api.put(`${endpoint}/${editingItem.id}`, { name: itemName });
        toast.success(`${modalType === 'counsellor' ? 'Counsellor' : 'Payment method'} updated`);
      } else {
        await api.post(endpoint, { name: itemName });
        toast.success(`${modalType === 'counsellor' ? 'Counsellor' : 'Payment method'} created`);
      }
      
      closeModal();
      fetchData();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleToggleStatus = async (type, item) => {
    try {
      const endpoint = type === 'counsellor' ? '/counsellors' : '/payment-methods';
      await api.put(`${endpoint}/${item.id}`, { is_active: !item.is_active });
      toast.success(`${item.is_active ? 'Deactivated' : 'Activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (type, item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    
    try {
      const endpoint = type === 'counsellor' ? '/counsellors' : '/payment-methods';
      await api.delete(`${endpoint}/${item.id}`);
      toast.success('Deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (user?.role !== 'Admin') {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6 text-center">
          <p className="text-gray-500">Only admins can access this page</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings2 className="w-7 h-7 text-green-600" />
            Teller Configuration
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage counsellors and payment methods</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Counsellors Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <User className="w-5 h-5 text-purple-600" />
                  Counsellors
                </h2>
                <Button size="sm" onClick={() => openModal('counsellor')}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
              
              {counsellors.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No counsellors configured</p>
              ) : (
                <div className="space-y-2">
                  {counsellors.map(c => (
                    <div 
                      key={c.id} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        c.is_active ? 'border-gray-200' : 'border-gray-100 bg-gray-50 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{c.name}</span>
                        {!c.is_active && (
                          <Badge className="bg-gray-100 text-gray-500 text-xs">Inactive</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openModal('counsellor', c)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit2 className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus('counsellor', c)}
                          className="h-8 w-8 p-0"
                        >
                          {c.is_active ? (
                            <PowerOff className="w-4 h-4 text-amber-600" />
                          ) : (
                            <Power className="w-4 h-4 text-green-600" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete('counsellor', c)}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Payment Methods Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-600" />
                  Payment Methods
                </h2>
                <Button size="sm" onClick={() => openModal('payment-method')}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
              
              {paymentMethods.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No payment methods configured</p>
              ) : (
                <div className="space-y-2">
                  {paymentMethods.map(m => (
                    <div 
                      key={m.id} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        m.is_active ? 'border-gray-200' : 'border-gray-100 bg-gray-50 opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{m.name}</span>
                        {!m.is_active && (
                          <Badge className="bg-gray-100 text-gray-500 text-xs">Inactive</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openModal('payment-method', m)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit2 className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus('payment-method', m)}
                          className="h-8 w-8 p-0"
                        >
                          {m.is_active ? (
                            <PowerOff className="w-4 h-4 text-amber-600" />
                          ) : (
                            <Power className="w-4 h-4 text-green-600" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete('payment-method', m)}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={!!modalType} onOpenChange={closeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit' : 'Add'} {modalType === 'counsellor' ? 'Counsellor' : 'Payment Method'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Name</Label>
            <Input
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              placeholder={`Enter ${modalType === 'counsellor' ? 'counsellor' : 'payment method'} name`}
              className="mt-1"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button onClick={handleSave}>{editingItem ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default TellerConfigPage;
